import { useState } from 'react'
import { Box, Grid, Stack, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import { industries } from '@/content/site'
import { industriesCopy, industryContent } from '@/content/copy'
import { productImagesFor } from '@/content/products'
import { industryIcons } from '@/lib/icons'
import { colors } from '@/theme'
import { Section } from '@/components/ui/Section'
import { SectionHeading } from '@/components/ui/SectionHeading'
import { Container } from '@/components/ui/Container'

// Resolve one representative image per industry for the hover backdrop.
const scenes = industries.map((ind) => {
  const c = industryContent[ind.slug as keyof typeof industryContent]
  const img = c ? productImagesFor(c.image.sku)[c.image.index] : undefined
  return { slug: ind.slug, src: img?.src }
})

/** Industries served — hover a tile and that industry's scene fades in behind the whole section. */
export function IndustriesStrip() {
  const [hovered, setHovered] = useState<string | null>(null)

  return (
    <Section disableContainer sx={{ position: 'relative', overflow: 'hidden' }}>
      {/* Hover backdrops */}
      {scenes.map((s) =>
        s.src ? (
          <Box
            key={s.slug}
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${s.src})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: hovered === s.slug ? 0.28 : 0,
              transition: 'opacity 600ms ease',
            }}
          />
        ) : null,
      )}
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, ${colors.bg} 0%, rgba(13,13,13,0.72) 100%)` }} />

      <Container sx={{ position: 'relative' }}>
        <SectionHeading eyebrow={industriesCopy.eyebrow} title={industriesCopy.title} description={industriesCopy.lede} />
        <Grid container spacing={{ xs: 2.5, md: 3 }}>
          {industries.map((industry) => {
            const Icon = industryIcons[industry.slug]
            return (
              <Grid key={industry.slug} size={{ xs: 6, sm: 4, md: 3 }}>
                <Box
                  component={RouterLink}
                  to={`/industries/${industry.slug}`}
                  onMouseEnter={() => setHovered(industry.slug)}
                  onMouseLeave={() => setHovered(null)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2.5,
                    height: '100%',
                    p: { xs: 2.5, md: 3 },
                    textDecoration: 'none',
                    borderRadius: 1,
                    border: `1px solid ${colors.border}`,
                    bgcolor: 'rgba(20,20,20,0.55)',
                    backdropFilter: 'blur(2px)',
                    transition: 'border-color 300ms ease, background-color 300ms ease',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(20,20,20,0.35)' },
                    '&:hover .ind-arrow': { opacity: 1, transform: 'translate(2px, -2px)' },
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box sx={{ color: 'primary.main' }}>{Icon && <Icon size={28} />}</Box>
                    <ArrowUpRight size={18} color={colors.grey} className="ind-arrow" style={{ opacity: 0, transition: 'opacity 300ms ease, transform 300ms ease' }} />
                  </Stack>
                  <Typography variant="h5" component="h3" sx={{ color: colors.white }}>
                    {industry.name}
                  </Typography>
                </Box>
              </Grid>
            )
          })}
        </Grid>
      </Container>
    </Section>
  )
}
